import { ButtonHTMLAttributes, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../common/Button";
import {
  Cog8ToothIcon,
  DocumentTextIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  LightBulbIcon,
  FolderIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";

interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  page: string;
  active?: boolean;
  onNavigate?: () => void;
  isMenuCollapsed?: boolean;
  // Unread count: a small blue pill at the right end when expanded, a dot on
  // the icon when collapsed. Nothing renders for 0.
  badge?: number;
}

function renderIcon(page: string, isMenuCollapsed?: boolean): ReactNode {
  switch (page) {
    case "cases":
      return (
        <DocumentTextIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "trash":
      return (
        <TrashIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "files":
      return (
        <FolderIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "forms":
      return (
        <DocumentDuplicateIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "redactions":
      return (
        <EyeSlashIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "feature-requests":
      return (
        <LightBulbIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "investigations":
      return (
        <MagnifyingGlassIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "policies":
      return (
        <ShieldCheckIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "academy":
      return (
        <AcademicCapIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    case "settings":
      return (
        <Cog8ToothIcon
          className={cn(!isMenuCollapsed ? "size-5 mr-1" : "size-6")}
        />
      );
    default:
      break;
  }
}

export default function MenuItem({
  children,
  className,
  page,
  active,
  onNavigate,
  isMenuCollapsed = false,
  badge,
  ...props
}: MenuItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // Allow callers to override the location-based active state (e.g. the Cases
  // item should only look active when the "All cases" filter is selected).
  const isActive =
    active !== undefined ? active : location.pathname.startsWith(`/${page}`);

  const handleClick = () => {
    navigate(`/${page}`);
    onNavigate?.();
  };

  const showBadge = typeof badge === "number" && badge > 0;

  return (
    <Button
      onClick={handleClick}
      className={cn(
        "btn border-0 w-full !min-h-10 shadow-none",
        // Collapsed, the icon is the only content — centre it in the rail
        // instead of leaving it hard against the left edge.
        isMenuCollapsed ? "justify-center px-0" : "justify-start",
        isActive &&
          "bg-blue-50 hover:bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:hover:bg-blue-900/40 dark:text-blue-300",
        !isActive && "hover:bg-base-300",
        !isMenuCollapsed && "h-[24px]",
        className
      )}
      {...props}
    >
      {!isMenuCollapsed ? (
        <div className={cn("flex items-center pl-2", showBadge && "w-full pr-1")}>
          {renderIcon(page, isMenuCollapsed)}
          {children}
          {showBadge && (
            <span
              className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-blue-500 text-white text-[10px] font-semibold leading-none tabular-nums"
              aria-label={`${badge} new`}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </div>
      ) : (
        <span className="relative inline-flex">
          {renderIcon(page, isMenuCollapsed)}
          {showBadge && (
            <span
              className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-base-100"
              aria-label={`${badge} new`}
            />
          )}
        </span>
      )}
    </Button>
  );
}
